// src/utils/regUtmStorage.js

const REG_UTM_KEY = 'sense_reg_utm_query';
const COOKIE_MAX_AGE_DAYS = 30; // Храним UTM 30 дней

// ============ Cookie helpers ============

/**
 * Устанавливает cookie с указанным именем, значением и сроком жизни в днях.
 */
function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // Используем path=/ чтобы cookie был доступен на всех страницах
    // SameSite=Lax для безопасности, но с возможностью чтения при навигации
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

/**
 * Получает значение cookie по имени.
 */
function getCookie(name) {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [cookieName, cookieValue] = cookie.trim().split('=');
        if (cookieName === name) {
            return decodeURIComponent(cookieValue || '');
        }
    }
    return null;
}

/**
 * Удаляет cookie по имени.
 */
function deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// ============ Main functions ============

/**
 * Считывает текущий location.search и сохраняет query string в cookie + localStorage.
 * 
 * Сохраняется:
 * - ?utm_source=instagram&utm_campaign=test (UTM-метки)
 * - ?instagram-sense (любой query string)
 * 
 * НЕ сохраняется:
 * - ?source=pwa (внутренние открытия PWA)
 * - Пустой query string
 * 
 * Сохранение происходит только один раз — если данные уже есть, не перезаписываем.
 */
export function storeRegUtmOnceFromLocation() {
    // Проверяем cookie (он общий между браузером и PWA)
    const existingCookie = getCookie(REG_UTM_KEY);
    // Также проверяем localStorage как fallback
    const existingLocal = localStorage.getItem(REG_UTM_KEY);

    // Если уже что-то сохранено — не трогаем
    if (existingCookie || existingLocal) {
        // Если есть в cookie, но нет в localStorage — синхронизируем
        if (existingCookie && !existingLocal) {
            localStorage.setItem(REG_UTM_KEY, existingCookie);
        }
        return;
    }

    const search = window.location.search || '';
    if (!search || search === '?') return;

    const params = new URLSearchParams(search);

    // Исключение 1: source=pwa (внутренние открытия из установленной PWA)
    const source = params.get('source');
    if (source && source.toLowerCase() === 'pwa') return;

    // Исключение 2: utm_source=pwa (на случай если PWA использует этот формат)
    const utmSource = params.get('utm_source');
    if (utmSource && utmSource.toLowerCase() === 'pwa') return;

    // Нормализуем строку к виду "?..."
    const normalized =
        search.startsWith('?') ? search : `?${search.replace(/^\?/, '')}`;

    // Сохраняем в cookie (общий между браузером и PWA) + localStorage
    setCookie(REG_UTM_KEY, normalized, COOKIE_MAX_AGE_DAYS);
    localStorage.setItem(REG_UTM_KEY, normalized);

    console.log('[regUtmStorage] Query params saved:', normalized);
}

/**
 * Возвращает сохранённую строку вида "?utm_source=...&utm_campaign=..."
 * или null, если ничего не сохранено.
 * 
 * Сначала проверяем cookie (он общий), потом localStorage как fallback.
 */
export function getStoredRegUtm() {
    // Приоритет: cookie > localStorage
    const fromCookie = getCookie(REG_UTM_KEY);
    if (fromCookie) {
        return fromCookie;
    }
    return localStorage.getItem(REG_UTM_KEY);
}

/**
 * Очищает сохранённые UTM метки из cookie и localStorage.
 * Вызывается после успешной отправки в RPC.
 */
export function clearStoredRegUtm() {
    deleteCookie(REG_UTM_KEY);
    localStorage.removeItem(REG_UTM_KEY);
    console.log('[regUtmStorage] UTM cleared after successful send');
}
